import { NewWarrantyCaseResponse, NewWarrantyCaseResponseType } from "@/api/rest/models/warranty";
import type { ApiChatMessage } from "@/api/rest/services/chatService";
import type { SubmitWarrantyArgs, WarrantyDraft, WarrantyPhase } from "@/api/rest/services/warrantyService";

export interface ChatMessageItem extends NewWarrantyCaseResponse {
  isResponse: boolean;
}

export interface ChatHistoryItem {
  role: "user" | "assistant";
  content: string;
}

export interface StructuredWarrantyDraftHydration {
  history: ChatHistoryItem[];
  phase: WarrantyPhase;
  selectedCategoryId: string | null;
  selectedCategoryName: string | null;
  submissionData: SubmitWarrantyArgs | null;
}

export const hydrateLegacyWarrantyChatHistory = (draft: WarrantyDraft): ChatMessageItem[] => {
  const currentStatus = draft.state?.currentStatus as Partial<NewWarrantyCaseResponse> | undefined;
  const textEntries = draft.entries.filter((entry) => entry.type === "TEXT");
  const lastAssistantIndex = (() => {
    for (let i = textEntries.length - 1; i >= 0; i -= 1) {
      if (textEntries[i].role === "ASSISTANT") {
        return i;
      }
    }
    return -1;
  })();

  const hydrated = textEntries.map((entry, index) => {
    const isResponse = entry.role === "ASSISTANT";
    const savedStatus = isResponse && index === lastAssistantIndex ? currentStatus : null;
    return {
      status: savedStatus?.status ?? NewWarrantyCaseResponseType.MORE_INFO_REQUIRED,
      message: savedStatus?.message ?? entry.content,
      caseId: savedStatus?.caseId ?? null,
      provisionalTitle: savedStatus?.provisionalTitle ?? null,
      otherIssues: savedStatus?.otherIssues ?? [],
      builderNote: savedStatus?.builderNote ?? null,
      draftId: draft.draftId,
      isResponse,
    };
  });

  if (hydrated.length === 0 && currentStatus?.message) {
    return [
      {
        status: currentStatus.status ?? NewWarrantyCaseResponseType.MORE_INFO_REQUIRED,
        message: currentStatus.message,
        caseId: currentStatus.caseId ?? null,
        provisionalTitle: currentStatus.provisionalTitle ?? null,
        otherIssues: currentStatus.otherIssues ?? [],
        builderNote: currentStatus.builderNote ?? null,
        draftId: draft.draftId,
        isResponse: true,
      },
    ];
  }

  return hydrated;
};

export const mapLegacyChatHistoryToApiMessages = (history: ChatMessageItem[]): ApiChatMessage[] => {
  return history
    .filter((m) => m.status === NewWarrantyCaseResponseType.MORE_INFO_REQUIRED)
    .map((item) => ({
      role: item.isResponse ? "assistant" : "user",
      content: item.message,
    }));
};

export const hydrateStructuredWarrantyDraft = (draft: WarrantyDraft): StructuredWarrantyDraftHydration => {
  const rawPhase = draft.state?.phase;
  const phase =
    rawPhase === "DETAIL_EXTRACTION" || rawPhase === "COMPLETED"
      ? (rawPhase as WarrantyPhase)
      : ("TRIAGE" as WarrantyPhase);

  return {
    history: draft.entries
      .filter((entry) => entry.type === "TEXT")
      .filter((entry) => entry.role === "USER" || entry.role === "ASSISTANT")
      .map((entry) => ({
        role: entry.role === "ASSISTANT" ? "assistant" : "user",
        content: entry.content,
      })),
    phase,
    selectedCategoryId: (draft.state?.selectedCategoryId as string | null | undefined) ?? null,
    selectedCategoryName: (draft.state?.selectedCategoryName as string | null | undefined) ?? null,
    submissionData: (draft.state?.submissionData as SubmitWarrantyArgs | null | undefined) ?? null,
  };
};
